import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Notice } from '@gatecheckpro/design-system';

/**
 * Keeps a rendering failure from taking the whole workspace with it.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * so before this existed a single bad assumption in one page produced a blank
 * white document: no header, no navigation, no message, nothing to click. That
 * happened here for real — a collection typed as a bare array instead of the
 * enveloped shape the API returns made `.length` undefined, and the resulting
 * `TypeError` blanked the workspace. The bug took a minute to fix and most of an
 * afternoon to find, because a blank page looks like a routing fault, a build
 * fault, or a session fault with equal plausibility.
 *
 * On event night that failure mode is worse than the defect causing it. An
 * operator who can still see the workspace and reach another page can keep
 * working around a broken screen; an operator looking at a white rectangle has
 * lost the product.
 *
 * **Nothing about the error reaches the screen.** Not the message, not the
 * stack, not a component name, not an identifier. A rendering failure can carry
 * anything that was in scope when it threw — an API payload, a token, a
 * person's details — and a sympathetic "here's what went wrong" is how that
 * gets shown to whoever is standing at the desk. The details go to the console
 * in development, where an engineer is the only reader.
 */
interface ErrorBoundaryProps {
  /** What failed, in the user's terms — "this page", not a component name. */
  readonly what: string;
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly failed: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Development only, and to the console rather than to the page. In a
    // production build this is silent by design: there is no reporting sink in
    // R1, and inventing one that ships error text off a device is a bigger
    // decision than a boundary gets to make.
    if (import.meta.env.DEV) {
      console.error('[GateCheckPro] render failed', error, info.componentStack);
    }
  }

  private readonly retry = (): void => this.setState({ failed: false });

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <Notice tone="critical" title={`${this.props.what} stopped working`} live>
        <div className="gc-stack gc-stack--tight">
          <p>
            Nothing you did caused this and nothing has been lost. The rest of the workspace still
            works, so you can carry on elsewhere while this is looked at.
          </p>
          <div className="gc-row">
            <Button variant="primary" onClick={this.retry}>
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload GateCheckPro</Button>
          </div>
        </div>
      </Notice>
    );
  }
}
